import { useEffect, useRef } from 'react';

interface LaTeXRendererProps {
  content: string;
  className?: string;
}

// Simple LaTeX to HTML converter for chemistry equations
// Supports: $equation$ for inline, $$equation$$ for display
// Chemical formulas: C_6H_6, H_2O, etc.
// Arrows: \rightarrow, \leftarrow, \rightleftharpoons

export const LaTeXRenderer = ({ content, className = '' }: LaTeXRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    
    // Convert LaTeX-like syntax to HTML
    const html = content
      // Display math $$...$$
      .replace(/\$\$(.+?)\$\$/g, '<div class="text-center my-2 text-lg">$1</div>')
      // Inline math $...$
      .replace(/\$(.+?)\$/g, '<span class="font-mono bg-slate-100 px-1 rounded">$1</span>')
      // Subscripts (e.g., H_2O)
      .replace(/([A-Za-z])(\d+)/g, '$1<sub class="text-xs">$2</sub>')
      // Chemical arrows
      .replace(/->|\\rightarrow/g, '→')
      .replace(/<-|\\leftarrow/g, '←')
      .replace(/<->|\\rightleftharpoons|\\leftrightarrow/g, '⇌')
      // Plus sign spacing
      .replace(/\+\s*/g, ' + ')
      // Delta symbol
      .replace(/\\Delta|Δ/g, 'Δ')
      // Heat symbol
      .replace(/\\Delta H|\$\\Delta H\$/g, 'ΔH')
      // Temperature
      .replace(/(\d+)°C/g, '$1°C');

    containerRef.current.innerHTML = html;
  }, [content]);

  return <div ref={containerRef} className={className} />;
};

// Example usage:
// <LaTeXRenderer content="$C_6H_6 + Cl_2 \rightarrow C_6H_5Cl + HCl$" />
// Output: C₆H₆ + Cl₂ → C₆H₅Cl + HCl
